import React, { useState, useMemo } from "react";
import { MdSearch, MdKeyboardArrowUp } from "react-icons/md";

const raleway = { fontFamily: "'Geist', sans-serif" };
const geistMono = { fontFamily: "'Geist Mono', monospace" };
const instrumentSerif = { fontFamily: "'Instrument Serif', Georgia, serif" };

const FAQS = [
  {
    category: "Accounts",
    question: "How do I open a Zikora Account?",
    answer:
      "Download the Zikora app, tap 'Create account', enter your BVN, NIN, and a valid email, then complete the in-app KYC step. Most accounts are activated within minutes.",
  },
  {
    category: "Accounts",
    question: "How do I update my personal details?",
    answer:
      "Go to Profile → Personal details inside the app. You can update your phone number, address, and next of kin without visiting a branch.",
  },
  {
    category: "Loans",
    question: "How do I apply for a loan?",
    answer:
      "From the app dashboard, tap 'Loans', pick the loan type, enter the amount and tenor, then submit. You'll get an in-app decision and the funds land in your Zikora account once approved.",
  },
  {
    category: "Loans",
    question: "What are the requirements for a Zikora loan?",
    answer:
      "An active Zikora account, a valid BVN, six months of salary or business inflow into the account, and no active loan default. Some loan products require additional documents.",
  },
  {
    category: "Z-ForHer",
    question: "Who is eligible for Z-ForHer?",
    answer:
      "Z-ForHer is open to women aged 18 and above who hold a Zikora account. It comes with preferential savings rates, female-focused loan products, and bespoke financial advice.",
  },
  {
    category: "Z-ForHer",
    question: "What benefits does Z-ForHer offer?",
    answer:
      "Members get higher savings interest, lower loan rates, exclusive community events, and access to a dedicated relationship officer for personalised support.",
  },
  {
    category: "Apps",
    question: "How do I download the Zikora app?",
    answer:
      "Search 'Zikora MFB' on the App Store or Google Play. Always confirm the publisher is Zikora Microfinance Bank before installing.",
  },
  {
    category: "Apps",
    question: "Why isn't my app updating?",
    answer:
      "Check that your phone is on the latest OS version, then uninstall and reinstall the app. If the issue persists, call our support line and we'll help you troubleshoot.",
  },
  {
    category: "Cards",
    question: "How do I request for a debit card?",
    answer:
      "Tap 'Cards' on the app dashboard, then 'Request new card'. Pick your preferred pickup branch or have it delivered — most cards are ready within 5 working days.",
  },
  {
    category: "Cards",
    question: "How do I activate my new card?",
    answer:
      "Open the app, go to Cards → Activate, then enter the last 4 digits of the card and set a 4-digit PIN at any ATM or via the app.",
  },
  {
    category: "Transfer",
    question: "What are the daily transfer limits?",
    answer:
      "Tier 1 accounts can move up to ₦300,000 per day. Fully verified accounts get a daily limit of ₦5,000,000, and you can request a higher limit via the app.",
  },
  {
    category: "Transfer",
    question: "How do I reverse a failed transfer?",
    answer:
      "Most failed transfers are auto-reversed within 30 minutes. If it takes longer, raise it via the Complaint form on this page and include the transaction reference.",
  },
  {
    category: "Transfer",
    question: "How do I save a beneficiary?",
    answer:
      "After making a transfer, tap 'Save as beneficiary' on the confirmation screen. You can also add beneficiaries directly under Transfers → Beneficiaries.",
  },
];

const CATEGORY_ORDER = [
  "All",
  "Accounts",
  "Loans",
  "Z-ForHer",
  "Apps",
  "Cards",
  "Transfer",
];

const HelpLibrary = () => {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [openIndex, setOpenIndex] = useState(null);

  const categoryCounts = useMemo(() => {
    const counts = { All: FAQS?.length };
    FAQS?.forEach((f) => {
      counts[f?.category] = (counts?.[f?.category] || 0) + 1;
    });
    return counts;
  }, []);

  const filtered = useMemo(() => {
    const q = query?.trim()?.toLowerCase();
    return FAQS?.filter((f) => {
      const matchCategory =
        activeCategory === "All" || f?.category === activeCategory;
      const matchQuery =
        !q ||
        f?.question?.toLowerCase()?.includes(q) ||
        f?.answer?.toLowerCase()?.includes(q) ||
        f?.category?.toLowerCase()?.includes(q);
      return matchCategory && matchQuery;
    });
  }, [query, activeCategory]);

  const toggle = (idx) => {
    setOpenIndex((current) => (current === idx ? null : idx));
  };

  return (
    <>
      <style>{`
				.support-faq-input::placeholder { color: #9CA3AF; }

				.support-faq-header-row {
					display: flex;
					align-items: flex-start;
					justify-content: space-between;
					gap: clamp(20px, 3vw, 48px);
					margin-bottom: clamp(28px, 3vw, 40px);
				}
				.support-faq-header-text { flex: 1 1 auto; min-width: 0; max-width: 640px; }
				.support-faq-search-wrap {
					flex: 0 0 auto;
					width: clamp(280px, 28vw, 360px);
					margin-top: clamp(60px, 6vw, 84px);
				}

				.support-faq-chips {
					display: flex;
					flex-wrap: wrap;
					gap: 10px;
					margin-bottom: clamp(28px, 3vw, 40px);
				}

				.support-faq-grid {
					display: grid;
					grid-template-columns: 1fr 1fr;
					gap: clamp(14px, 1.4vw, 20px);
				}

				.support-faq-card {
					background: #FFFFFF;
					border: 1px solid #E5E9E6;
					border-radius: 14px;
					padding: clamp(18px, 1.6vw, 22px);
					cursor: pointer;
					transition: border-color 200ms ease;
					text-align: left;
					font: inherit;
					color: inherit;
					box-sizing: border-box;
				}
				.support-faq-card:hover { border-color: #86C7A8; }
				.support-faq-chevron {
					transition: transform 240ms ease;
				}
				.support-faq-card[data-open="true"] .support-faq-chevron {
					transform: rotate(180deg);
				}

				@media (max-width: 900px) {
					.support-faq-header-row { flex-direction: column !important; }
					.support-faq-search-wrap { width: 100% !important; margin-top: 0 !important; }
					.support-faq-grid { grid-template-columns: 1fr !important; }
				}
			`}</style>

      <section
        id="help-library"
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding:
            "clamp(56px, 6.94vw, 100px) clamp(20px, 5.56vw, 80px)",
        }}
      >
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          {/* Header — left-aligned text + right-aligned search */}
          <div className="support-faq-header-row">
            <div className="support-faq-header-text">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "clamp(12px, 1.4vw, 20px)",
                }}
              >
                <span
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#295B4A",
                    display: "inline-block",
                  }}
                />
                <span
                  style={{
                    ...geistMono,
                    fontWeight: 500,
                    fontSize: "12px",
                    lineHeight: 1,
                    letterSpacing: "0.1em",
                    color: "#295B4A",
                    textTransform: "uppercase",
                  }}
                >
                  Frequently asked questions
                </span>
              </div>

              <h2
                style={{
                  ...instrumentSerif,
                  fontWeight: 400,
                  fontSize: "clamp(28px, 3.9vw, 56px)",
                  lineHeight: 1.1,
                  letterSpacing: "-0.01em",
                  color: "#132A22",
                  margin: "0 0 clamp(10px, 1vw, 14px) 0",
                }}
              >
                Search the help library
              </h2>

              <p
                style={{
                  ...raleway,
                  fontWeight: 400,
                  fontSize: "clamp(14px, 1.1vw, 16px)",
                  lineHeight: 1.6,
                  color: "#6B7B70",
                  margin: 0,
                }}
              >
                Browse by category or search for a specific question.
              </p>
            </div>

            {/* Search */}
            <div className="support-faq-search-wrap">
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <MdSearch
                  size={18}
                  style={{
                    position: "absolute",
                    left: "16px",
                    color: "#9CA3AF",
                    pointerEvents: "none",
                  }}
                />
                <input
                  type="text"
                  className="support-faq-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search questions, e.g 'PIN', 'transfer limit'"
                  style={{
                    ...raleway,
                    width: "100%",
                    height: "48px",
                    padding: "0 18px 0 44px",
                    background: "#FFFFFF",
                    border: "1px solid #E5E9E6",
                    borderRadius: "9999px",
                    fontSize: "13px",
                    color: "#132A22",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Category chips */}
          <div className="support-faq-chips">
            {CATEGORY_ORDER?.map((cat) => {
              const isActive = activeCategory === cat;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => {
                    setActiveCategory(cat);
                    setOpenIndex(null);
                  }}
                  style={{
                    ...raleway,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    height: "36px",
                    padding: "0 16px",
                    background: isActive ? "#132A22" : "#FFFFFF",
                    border: isActive
                      ? "1px solid #132A22"
                      : "1px solid #E5E9E6",
                    borderRadius: "9999px",
                    fontWeight: 500,
                    fontSize: "13px",
                    color: isActive ? "#FFFFFF" : "#132A22",
                    cursor: "pointer",
                    transition:
                      "background 200ms ease, color 200ms ease, border-color 200ms ease",
                  }}
                >
                  {cat}
                  <span
                    style={{
                      ...geistMono,
                      fontWeight: 500,
                      fontSize: "11px",
                      color: isActive
                        ? "rgba(255,255,255,0.6)"
                        : "#9CA3AF",
                    }}
                  >
                    {categoryCounts?.[cat] || 0}
                  </span>
                </button>
              );
            })}
          </div>

          {/* FAQ grid */}
          {filtered?.length > 0 ? (
            <div className="support-faq-grid">
              {filtered?.map((f, idx) => {
                const isOpen = openIndex === idx;
                return (
                  <button
                    key={`${f?.category}-${f?.question}-${idx}`}
                    type="button"
                    className="support-faq-card"
                    data-open={isOpen}
                    onClick={() => toggle(idx)}
                    aria-expanded={isOpen}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          ...geistMono,
                          fontWeight: 500,
                          fontSize: "11px",
                          letterSpacing: "0.1em",
                          color: "#295B4A",
                          textTransform: "uppercase",
                        }}
                      >
                        {f?.category}
                      </span>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "16px",
                        }}
                      >
                        <span
                          style={{
                            ...raleway,
                            fontWeight: 500,
                            fontSize: "15px",
                            lineHeight: 1.4,
                            color: "#132A22",
                          }}
                        >
                          {f?.question}
                        </span>

                        <span
                          style={{
                            flexShrink: 0,
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            background: "#F1F4F0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#295B4A",
                          }}
                        >
                          <MdKeyboardArrowUp
                            size={18}
                            className="support-faq-chevron"
                          />
                        </span>
                      </div>

                      {isOpen && (
                        <p
                          style={{
                            ...raleway,
                            fontWeight: 400,
                            fontSize: "13px",
                            lineHeight: 1.65,
                            color: "#6B7B70",
                            margin: "8px 0 0 0",
                          }}
                        >
                          {f?.answer}
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <p
              style={{
                ...raleway,
                fontSize: "14px",
                color: "#6B7B70",
                textAlign: "center",
                padding: "40px 0",
              }}
            >
              No matches yet — try a different keyword or{" "}
              <a
                href="#support-concern"
                style={{ color: "#295B4A", fontWeight: 600 }}
              >
                ask our team
              </a>
              .
            </p>
          )}
        </div>
      </section>
    </>
  );
};

export default HelpLibrary;
